import React, { useState, useEffect } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { Page, NamedLink, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ProviderSignupPage.module.css';

/**
 * Provider Signup Page - Marketing/conversion landing page
 * This is the dedicated onboarding flow for new providers (vendors who want to list party kits)
 */
const ProviderSignupPage = ({ currentUser, params, history, location, ...rest }) => {
  // Interactive calculator state
  const [calculator, setCalculator] = useState({
    category: 'birthday',
    investment: 150
  });

  const [calculatorResults, setCalculatorResults] = useState(null);

  // Category multipliers
  const categoryMultipliers = {
    birthday: 1.0,
    bridal_baby_shower: 1.3,
    milestone: 1.2,
    themed_dinners: 0.9,
    others: 1.0
  };

  // Calculate earnings when inputs change
  useEffect(() => {
    const multiplier = categoryMultipliers[calculator.category] || 1.0;
    const kitValue = calculator.investment;
    const perRental = Math.round(kitValue * 0.20 * multiplier); // 20% of kit value per rental
    const perRentalMin = Math.round(perRental * 0.8);
    const perRentalMax = Math.round(perRental * 1.2);
    const bookingsPerMonth = 4; // Conservative estimate
    const breakEvenMonths = Math.ceil(kitValue / (perRental * bookingsPerMonth));
    const annualEarningsMin = Math.round(perRentalMin * bookingsPerMonth * 12);
    const annualEarningsMax = Math.round(perRentalMax * bookingsPerMonth * 12);

    setCalculatorResults({
      kitValue,
      perRentalMin,
      perRentalMax,
      breakEvenMonths: Math.min(breakEvenMonths, 6), // Cap at 6 months
      annualEarningsMin,
      annualEarningsMax
    });
  }, [calculator]);

  const handleCalculatorChange = (field, value) => {
    setCalculator(prev => ({ ...prev, [field]: value }));
  };

  const scrollToCalculator = () => {
    document.getElementById('earnings-calculator')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  const title = 'Turn Your Party Supplies Into Income - Merrily';
  const description = 'Join as a provider and earn money renting out your party supplies. The average kit earns $25-35 per rental.';

  return (
    <Page
      title={title}
      description={description}
      className={css.root}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="ProviderSignupPage" />}
        footer={<FooterContainer />}
      >
        {/* Hero Section */}
        <section className={css.heroSection}>
          <div className={css.container}>
            <h1 className={css.heroTitle}>
              <FormattedMessage id="ProviderSignupPage.heroTitle" />
            </h1>
            
            <p className={css.heroSubtitle}>
              <FormattedMessage id="ProviderSignupPage.heroSubtitle" />
            </p>
            
            <button 
              className={css.heroCTA}
              onClick={scrollToCalculator}
            >
              <FormattedMessage id="ProviderSignupPage.heroCTA" />
            </button>
          </div>
        </section>

        {/* Value Props Section */}
        <section className={css.valuePropsSection}>
          <div className={css.container}>
            <div className={css.valuePropsGrid}>
              <div className={css.valueProp}>
                <div className={css.valuePropIcon}>💰</div>
                <h3 className={css.valuePropTitle}>
                  <FormattedMessage id="ProviderSignupPage.earnIncomeTitle" />
                </h3>
                <p className={css.valuePropText}>
                  <FormattedMessage id="ProviderSignupPage.earnIncomeText" />
                </p>
              </div>

              <div className={css.valueProp}>
                <div className={css.valuePropIcon}>📦</div>
                <h3 className={css.valuePropTitle}>
                  <FormattedMessage id="ProviderSignupPage.easyListTitle" />
                </h3>
                <p className={css.valuePropText}>
                  <FormattedMessage id="ProviderSignupPage.easyListText" />
                </p>
              </div>

              <div className={css.valueProp}>
                <div className={css.valuePropIcon}>🛡️</div>
                <h3 className={css.valuePropTitle}>
                  <FormattedMessage id="ProviderSignupPage.protectedTitle" />
                </h3>
                <p className={css.valuePropText}>
                  <FormattedMessage id="ProviderSignupPage.protectedText" />
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className={css.howItWorksSection}>
          <div className={css.container}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="ProviderSignupPage.howItWorksTitle" />
            </h2>
            
            <div className={css.stepsContainer}>
              <div className={css.step}>
                <div className={css.stepIcon}>🎉</div>
                <div className={css.stepContent}>
                  <h3 className={css.stepTitle}>
                    <FormattedMessage id="ProviderSignupPage.step1Title" />
                  </h3>
                  <p className={css.stepText}>
                    <FormattedMessage id="ProviderSignupPage.step1Text" />
                  </p>
                </div>
              </div>

              <div className={css.step}>
                <div className={css.stepIcon}>📸</div>
                <div className={css.stepContent}>
                  <h3 className={css.stepTitle}>
                    <FormattedMessage id="ProviderSignupPage.step2Title" />
                  </h3>
                  <p className={css.stepText}>
                    <FormattedMessage id="ProviderSignupPage.step2Text" />
                  </p>
                </div>
              </div>

              <div className={css.step}>
                <div className={css.stepIcon}>📅</div>
                <div className={css.stepContent}>
                  <h3 className={css.stepTitle}>
                    <FormattedMessage id="ProviderSignupPage.step3Title" />
                  </h3>
                  <p className={css.stepText}>
                    <FormattedMessage id="ProviderSignupPage.step3Text" />
                  </p>
                </div>
              </div>

              <div className={css.step}>
                <div className={css.stepIcon}>💵</div>
                <div className={css.stepContent}>
                  <h3 className={css.stepTitle}>
                    <FormattedMessage id="ProviderSignupPage.step4Title" />
                  </h3>
                  <p className={css.stepText}>
                    <FormattedMessage id="ProviderSignupPage.step4Text" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Earnings Calculator */}
        <section id="earnings-calculator" className={css.calculatorSection}>
          <div className={css.container}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="ProviderSignupPage.calculatorTitle" />
            </h2>
            
            <div className={css.calculatorContainer}>
              <div className={css.calculatorInputs}>
                <div className={css.inputGroup}>
                  <label className={css.inputLabel}>
                    <FormattedMessage id="ProviderSignupPage.categoryLabel" />
                  </label>
                  <select
                    value={calculator.category}
                    onChange={(e) => handleCalculatorChange('category', e.target.value)}
                    className={css.selectInput}
                  >
                    <option value="birthday">Birthday</option>
                    <option value="bridal_baby_shower">Bridal/Baby Shower</option>
                    <option value="milestone">Milestone</option>
                    <option value="themed_dinners">Themed Dinners</option>
                    <option value="others">Others</option>
                  </select>
                </div>

                <div className={css.inputGroup}>
                  <label className={css.inputLabel}>
                    <FormattedMessage id="ProviderSignupPage.investmentLabel" />
                  </label>
                  <input
                    type="number"
                    value={calculator.investment}
                    onChange={(e) => handleCalculatorChange('investment', parseInt(e.target.value) || 0)}
                    className={css.numberInput}
                    min="50"
                    max="1000"
                    step="25"
                  />
                </div>
              </div>

              {calculatorResults && (
                <div className={css.calculatorResults}>
                  <div className={css.resultRow}>
                    <span className={css.resultIcon}>📦</span>
                    <span className={css.resultLabel}>Estimated kit value:</span>
                    <span className={css.resultValue}>~${calculatorResults.kitValue}</span>
                  </div>

                  <div className={css.resultRow}>
                    <span className={css.resultIcon}>💰</span>
                    <span className={css.resultLabel}>Per rental:</span>
                    <span className={css.resultValue}>
                      ${calculatorResults.perRentalMin}-${calculatorResults.perRentalMax}/day
                    </span>
                  </div>

                  <div className={css.resultRow}>
                    <span className={css.resultIcon}>📅</span>
                    <span className={css.resultLabel}>Est. bookings:</span>
                    <span className={css.resultValue}>3-5/month</span>
                  </div>

                  <div className={css.resultRow}>
                    <span className={css.resultIcon}>🔄</span>
                    <span className={css.resultLabel}>Breakeven:</span>
                    <span className={css.resultValue}>~{calculatorResults.breakEvenMonths} months</span>
                  </div>

                  <div className={css.resultRowHighlight}>
                    <span className={css.resultIcon}>📊</span>
                    <span className={css.resultLabel}>Annual earnings:</span>
                    <span className={css.resultValueHighlight}>
                      ${calculatorResults.annualEarningsMin.toLocaleString()}-{calculatorResults.annualEarningsMax.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className={css.trustSection}>
          <div className={css.container}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="ProviderSignupPage.trustTitle" />
            </h2>
            
            <div className={css.trustContent}>
              <div className={css.trustFeatures}>
                <div className={css.trustFeature}>
                  <div className={css.trustIcon}>📷</div>
                  <h4 className={css.trustFeatureTitle}>
                    <FormattedMessage id="ProviderSignupPage.photoVerificationTitle" />
                  </h4>
                  <p className={css.trustFeatureText}>
                    <FormattedMessage id="ProviderSignupPage.photoVerificationText" />
                  </p>
                </div>

                <div className={css.trustFeature}>
                  <div className={css.trustIcon}>💳</div>
                  <h4 className={css.trustFeatureTitle}>
                    <FormattedMessage id="ProviderSignupPage.depositProtectionTitle" />
                  </h4>
                  <p className={css.trustFeatureText}>
                    <FormattedMessage id="ProviderSignupPage.depositProtectionText" />
                  </p>
                </div>

                <div className={css.trustFeature}>
                  <div className={css.trustIcon}>⭐</div>
                  <h4 className={css.trustFeatureTitle}>
                    <FormattedMessage id="ProviderSignupPage.trustedPlatformTitle" />
                  </h4>
                  <p className={css.trustFeatureText}>
                    <FormattedMessage id="ProviderSignupPage.trustedPlatformText" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stripe Connect Explainer */}
        <section className={css.stripeSection}>
          <div className={css.container}>
            <div className={css.stripeExplainer}>
              <div className={css.stripeIcon}>💰</div>
              <h2 className={css.stripeTitle}>
                <FormattedMessage id="ProviderSignupPage.stripeTitle" />
              </h2>
              
              <p className={css.stripeIntro}>
                <FormattedMessage id="ProviderSignupPage.stripeIntro" />
              </p>

              <div className={css.stripeRequirements}>
                <div className={css.requirement}>
                  <span className={css.checkmark}>✅</span>
                  <span className={css.requirementText}>
                    <FormattedMessage id="ProviderSignupPage.requirementId" />
                  </span>
                </div>
                
                <div className={css.requirement}>
                  <span className={css.checkmark}>✅</span>
                  <span className={css.requirementText}>
                    <FormattedMessage id="ProviderSignupPage.requirementBank" />
                  </span>
                </div>
                
                <div className={css.requirement}>
                  <span className={css.checkmark}>✅</span>
                  <span className={css.requirementText}>
                    <FormattedMessage id="ProviderSignupPage.requirementInfo" />
                  </span>
                </div>
              </div>

              <p className={css.stripeAssurance}>
                <FormattedMessage id="ProviderSignupPage.stripeAssurance" />
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className={css.finalCTASection}>
          <div className={css.container}>
            <h2 className={css.finalCTATitle}>
              <FormattedMessage id="ProviderSignupPage.finalCTATitle" />
            </h2>
            
            <div className={css.finalCTAButtons}>
              <NamedLink name="SignupPage" className={css.primaryCTAButton}>
                <FormattedMessage id="ProviderSignupPage.signupButton" />
              </NamedLink>
              
              <NamedLink name="SearchPage" className={css.secondaryCTAButton}>
                <FormattedMessage id="ProviderSignupPage.browseButton" />
              </NamedLink>
            </div>
            
            <p className={css.finalCTADisclaimer}>
              <FormattedMessage id="ProviderSignupPage.disclaimer" />
            </p>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ProviderSignupPage;